import { ApiError } from '@/app/lib/apiError'
import { QuantRunStatusBadge } from '@/app/components/QuantRunStatusBadge'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { QuantLogPanel } from '@/app/components/QuantLogPanel'
import { QuantRunChartPanel } from '@/app/components/QuantRunChartPanel'
import { QuantRunParams } from '@/app/components/QuantRunParams'
import { QuantRunSparkline } from '@/app/components/QuantRunSparkline'
import { useNowTick } from '@/app/hooks/useNowTick'
import { refreshQuantRuns } from '@/app/hooks/useQuantRuns'
import { refreshQuantStrategies } from '@/app/hooks/useQuantStrategies'
import { useQuantRun } from '@/app/hooks/useQuantRun'
import { useQuantRunChart } from '@/app/hooks/useQuantRunChart'
import { useQuantRunLog } from '@/app/hooks/useQuantRunLog'
import { flagsFor, saveConfigForm, setLoadedNote } from '@/app/lib/quant/configFormState'
import { paramsForConfig } from '@/app/lib/quant/paramsForConfig'
import {
  chartEmptyText,
  chartTabAvailable,
  initialChartTabState,
  nextChartTabState,
  type RunTab,
} from '@/app/lib/quant/chartTab'
import { quantApi } from '@/app/lib/quant/quantApi'
import { hasMetrics, hasStoredEquity, metricsCells } from '@/app/lib/quant/runMetrics'
import { isAlreadyGone, isRunStillRunning, quantErrorMessage } from '@/app/lib/quant/quantErrors'
import { observeRun } from '@/app/lib/quant/runFinish'
import { schemaToParams } from '@/app/lib/quant/schemaToParams'
import { type ParamDef, type ParamValue, type RunDetail, type Strategy } from '@/app/lib/quant/types'
import { useSignalLog } from '@/app/lib/signalLog'
import { Button } from '@/ui/Button'
import { ConfirmButton } from '@/ui/ConfirmButton'
import { cn } from '@/ui/lib/utils'
import {
  formatDuration,
  formatReturn,
  formatTimestamp,
  returnTone,
  TONE_CLASS,
} from '@/ui/lib/quantFormat'
import { QuantRunMetrics } from '@/ui/QuantRunMetrics'
import { QuantUnavailable } from '@/ui/QuantUnavailable'

function BackLink({ slug }: { slug: string }) {
  return (
    <Link
      to={`/strategies/${slug}/runs`}
      className="font-mono text-[11px] tracking-[0.1em] text-paper-50 uppercase no-underline hover:text-paper"
    >
      ← All runs
    </Link>
  )
}

/** Run detail (spec 036 D-4): header, summary row, and the Params / Log panels. */
export function QuantRunDetail({ strategy, runId }: { strategy: Strategy; runId: string }) {
  const state = useQuantRun(runId)
  const { emit } = useSignalLog()
  const run = state.status === 'ready' ? state.run : null

  useEffect(() => {
    if (run !== null) observeRun(run, emit)
  }, [run, emit])
  // A status change seen here refreshes the list store so counts/badges agree.
  const status = run?.status
  const prevStatus = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (status !== undefined && status !== 'running') void refreshQuantRuns()
    // A run this page saw running that has now ended changes the strategy's counts (AC 32).
    if (prevStatus.current === 'running' && status !== undefined && status !== 'running')
      refreshQuantStrategies()
    prevStatus.current = status
  }, [status])

  if (state.status === 'loading') return <div data-pane="runs" className="absolute inset-0" />
  if (state.status === 'not_found') {
    return (
      <div
        data-pane="runs"
        className="absolute inset-0 flex flex-col gap-3 px-7 pt-6 font-mono text-[12px] text-paper-50"
      >
        <span role="status">Run not found</span>
        <BackLink slug={strategy.slug} />
      </div>
    )
  }
  if (state.status === 'error') {
    return (
      <div
        data-pane="runs"
        className="absolute inset-0 flex flex-col gap-3 px-7 pt-6 font-mono text-[12px]"
      >
        <QuantUnavailable message={state.message} onRetry={state.reload} />
        <BackLink slug={strategy.slug} />
      </div>
    )
  }
  return (
    <RunView strategy={strategy} run={state.run} reload={state.reload} pollError={state.error} />
  )
}

function RunView({
  strategy,
  run,
  reload,
  pollError,
}: {
  strategy: Strategy
  run: RunDetail
  reload: () => void
  pollError?: string
}) {
  const navigate = useNavigate()
  const { emit } = useSignalLog()
  // With no readable schema (strategies unavailable, or a strategy.toml that failed to parse)
  // the run's own recorded params are listed as they are, without defaults to compare to.
  const schemaDefs = useMemo(() => schemaToParams(strategy.params_schema), [strategy.params_schema])
  const noSchema = schemaDefs.length === 0 && Object.keys(run.params).length > 0
  const defs = useMemo<ParamDef[]>(
    () =>
      noSchema
        ? Object.entries(run.params).map(([name, value]) => ({
            name,
            kind: typeof value === 'boolean' ? 'bool' : typeof value === 'number' ? 'float' : 'str',
            default: value,
            help: '',
            options: [],
          }))
        : schemaDefs,
    [noSchema, run.params, schemaDefs],
  )
  const running = run.status === 'running'
  // echo-core 0.63.0 reports has_chart false while a run is running and true only once it ended.
  // Spec 038 P29: a run with only a stored equity curve also has something to show on the Chart tab.
  const storedEquity = hasStoredEquity(run)
  const chartAvailable = chartTabAvailable(run.has_chart, storedEquity)
  // Spec 038 P11: the tab and the chart-request logic are one state, advanced during render.
  const [view, setView] = useState(() => {
    // The first tab depends only on whether something can be shown on Chart; a just-started run has
    // nothing yet, so it opens on Log by itself (no router state, which would survive a reload).
    const tab: RunTab = chartAvailable ? 'chart' : 'log'
    return { tab, ctl: initialChartTabState({ status: run.status, chartAvailable, tab }) }
  })
  const step = nextChartTabState(view.ctl, { status: run.status, chartAvailable, tab: view.tab })
  const nextTab: RunTab = step.selectChart ? 'chart' : view.tab
  if (
    nextTab !== view.tab ||
    step.state.sawRunning !== view.ctl.sawRunning ||
    step.state.prevAvailable !== view.ctl.prevAvailable ||
    step.state.switched !== view.ctl.switched ||
    step.state.requested !== view.ctl.requested
  ) {
    setView({ tab: nextTab, ctl: step.state })
  }
  const tab = nextTab
  const chartReady = chartAvailable
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const canceling = Boolean(run.cancel_requested_at) && running
  const chart = useQuantRunChart(
    run.id,
    strategy.slug,
    step.state.requested && run.has_chart,
    reload,
  )
  const now = useNowTick(running)
  const emptyText = chartEmptyText(run.status, chartReady)

  const startMs = run.started_at === null ? null : Date.parse(run.started_at)
  const endMs = run.finished_at !== null ? Date.parse(run.finished_at) : running ? now : null
  const durationMs = startMs !== null && endMs !== null ? endMs - startMs : null
  const tone = run.status === 'completed' ? returnTone(run.return_pct) : 'none'

  async function cancel() {
    setCancelError(null)
    observeRun(run, emit) // make sure the running state was seen before it flips
    try {
      // echo-core 0.63.0 answers at once with the run still running; the stop lands shortly after.
      const done = await quantApi.cancelRun(run.id)
      observeRun(done, emit)
      if (done.status === 'running') {
        emit('warn', `Cancel requested for run ${run.id} - waiting for it to stop.`)
      }
      reload()
      void refreshQuantRuns()
      refreshQuantStrategies()
    } catch (error) {
      // A failed cancel is not pending: show the server's words and keep the button enabled.
      setCancelError(quantErrorMessage(error))
      reload()
    }
  }

  async function deleteRun() {
    setDeleteError(null)
    try {
      await quantApi.deleteRun(run.id)
    } catch (error) {
      // A 404 means it is already gone, which is what the operator wanted.
      if (!isAlreadyGone(error)) {
        setDeleteError(quantErrorMessage(error))
        if (isRunStillRunning(error) && error instanceof ApiError) {
          emit('warn', `Run ${run.id} was not deleted - ${error.message}`)
          reload()
        }
        return
      }
    }
    emit('save', `Deleted run ${run.id}.`)
    void refreshQuantRuns()
    refreshQuantStrategies()
    navigate(`/strategies/${strategy.slug}/runs`)
  }

  function loadParams() {
    const raw: Record<string, ParamValue> = {}
    const { kept, dropped } = paramsForConfig(run.params, schemaDefs)
    for (const def of defs) {
      const value = def.name in kept ? kept[def.name] : def.default
      raw[def.name] = def.kind === 'bool' ? value === true : value === null ? '' : String(value)
    }
    saveConfigForm(strategy.slug, { raw, lastValidFlags: flagsFor(defs, raw) ?? '' })
    setLoadedNote(strategy.slug, dropped)
    emit('save', `Loaded run ${run.id} params into the ${strategy.slug} config.${dropped.length > 0 ? ` (${dropped.length} dropped)` : ''}`)
    navigate(`/strategies/${strategy.slug}/config`)
  }

  const summary: Array<[string, string]> = [
    ['Started', formatTimestamp(run.started_at)],
    ['Duration', formatDuration(durationMs)],
    ['Trades', run.trades === null ? '—' : String(run.trades)],
    ['Exit code', run.exit_code === null ? '—' : String(run.exit_code)],
    ['Container', run.container_name ?? '—'],
  ]

  return (
    <div data-pane="runs" data-run-id={run.id} className="absolute inset-0 flex min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-3.5 px-7 pt-3.5 pb-2.5">
        <BackLink slug={strategy.slug} />
        <QuantRunStatusBadge status={run.status} cancelRequestedAt={run.cancel_requested_at} />
        <span
          className="min-w-0 flex-1 truncate font-mono text-[11px] text-paper-40"
          title={run.id}
        >
          {run.id}
        </span>
        {cancelError !== null && (
          <span role="alert" className="font-mono text-[11px] text-alert">
            {cancelError}
          </span>
        )}
        {deleteError !== null && (
          <span role="alert" className="font-mono text-[11px] text-alert">
            {deleteError}
          </span>
        )}
        {running && (
          <Button
            aria-label="Cancel run"
            size="sm"
            disabled={canceling}
            aria-busy={canceling}
            onClick={() => void cancel()}
            className="min-w-[104px] border-alert bg-alert text-paper disabled:border-paper-35 disabled:bg-transparent disabled:text-paper-50"
          >
            {canceling ? 'Canceling…' : 'Cancel'}
          </Button>
        )}
        <span title={running ? 'Cancel the run before deleting it.' : undefined}>
          <ConfirmButton
            label="DELETE RUN"
            size="sm"
            disabled={running}
            onArm={() => emit('warn', 'Delete armed for this run - press again to confirm.')}
            onConfirm={() => void deleteRun()}
          />
        </span>
      </div>

      {run.error !== null && run.error !== '' && (
        <div
          role="status"
          data-run-error
          className="shrink-0 truncate px-7 pb-2.5 font-mono text-[11px] leading-[16px] text-alert"
          title={run.error}
        >
          {run.error}
        </div>
      )}
      {pollError !== undefined && (
        <div className="shrink-0 px-7 pb-2.5">
          <QuantUnavailable message={pollError} onRetry={reload} />
        </div>
      )}

      <div
        data-run-summary
        className="flex shrink-0 flex-wrap items-center gap-x-[26px] gap-y-2 border-y border-paper-14 px-7 py-3"
      >
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-[9px] tracking-[0.16em] text-paper-40 uppercase">
            Return
          </span>
          <span className={cn('min-w-[88px] font-mono text-[20px] leading-none', TONE_CLASS[tone])}>
            {run.status === 'completed' ? formatReturn(run.return_pct) : '—'}
          </span>
        </div>
        <QuantRunSparkline run={run} width={240} height={36} />
        {summary.map(([label, value]) => (
          <div key={label} className="flex min-w-0 flex-col gap-0.5">
            <span className="font-mono text-[9px] tracking-[0.16em] text-paper-40 uppercase">
              {label}
            </span>
            <span
              className="max-w-[260px] truncate font-mono text-[12px] text-paper-90"
              title={value}
              data-summary={label}
            >
              {value}
            </span>
          </div>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {hasMetrics(run) && <QuantRunMetrics cells={metricsCells(run)} />}
        <div
          role="tablist"
          aria-label="Run panels"
          className="flex h-10 shrink-0 gap-5 border-b border-paper-14 px-7"
        >
          {(
            [
              ['chart', 'Chart'],
              ['log', 'Log'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => {
                setView((v) => ({ ...v, tab: id }))
              }}
              className={cn(
                'cursor-pointer border-0 border-b-2 bg-transparent py-0 font-mono text-[11px] tracking-[0.14em] uppercase',
                tab === id
                  ? 'border-accent-line text-paper'
                  : 'border-transparent text-paper-50 hover:text-paper',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div data-region="panel" className="relative min-h-[300px] flex-1 border-b border-paper-14">
          {tab === 'log' && <RunLogPanel runId={run.id} />}
          {tab === 'chart' &&
            (emptyText !== null ? (
              <div
                data-panel="chart-empty"
                className="absolute inset-0 flex items-center justify-center bg-ink px-7"
              >
                <span role="status" className="font-mono text-[12px] text-paper-50">
                  {emptyText}
                </span>
              </div>
            ) : (
              <QuantRunChartPanel
                chart={chart}
                hasChart={run.has_chart}
                equityCurve={storedEquity ? run.equity_curve : null}
              />
            ))}
        </div>
        <QuantRunParams defs={defs} params={run.params} noSchema={noSchema} onLoadParams={loadParams} />
      </div>
    </div>
  )
}

function RunLogPanel({ runId }: { runId: string }) {
  const log = useQuantRunLog(runId)
  return <QuantLogPanel log={log} ariaLabel="Run log" />
}
